import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Mail, Send, Loader2, Inbox } from "lucide-react";
import { toast, Toaster } from "sonner";

export default function DigestPreview() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testEmail, setTestEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [running, setRunning] = useState(false);

  function load() {
    setLoading(true);
    api.get("/admin/digest/preview")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message))
      .finally(() => setLoading(false));
  }
  useEffect(load, []);

  async function runDigest() {
    setRunning(true);
    try {
      const { data: r } = await api.post("/admin/digest/run");
      toast.success(`Digest run: ${r.recipients} recipient${r.recipients === 1 ? "" : "s"}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setRunning(false);
    }
  }

  async function sendTest() {
    if (!testEmail) return;
    setSending(true);
    try {
      await api.post("/admin/digest/test-send", null, { params: { email: testEmail } });
      toast.success(`Test digest queued to ${testEmail}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="p-8 space-y-6 max-w-5xl" data-testid="digest-preview-page">
      <Toaster position="top-right" />
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#c15337]">Communications</div>
          <h1 className="font-heading text-3xl font-bold mt-1 text-[#114232] flex items-center gap-2">
            <Mail className="w-7 h-7" /> Email Digest Preview
          </h1>
          <p className="text-sm text-[#5c6360] mt-1 max-w-xl">
            Preview the next 24-hour unread-notifications digest. Fire it on demand or send a test
            to your own inbox to verify the email pipeline.
          </p>
        </div>
        <Button
          onClick={runDigest}
          disabled={running}
          data-testid="digest-run-btn"
          className="btn-baobab rounded-full px-5 h-11 hover:bg-[var(--baobab-hover)]"
        >
          {running ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
          Run digest now
        </Button>
      </header>

      {loading && (
        <div className="text-center py-10 text-[#5c6360]">
          <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading preview…
        </div>
      )}

      {!loading && data && (
        <>
          {/* Would-email recipients */}
          <div className="bg-white border border-border rounded-md" data-testid="digest-would-email">
            <div className="px-5 py-4 border-b border-border flex items-center justify-between">
              <div className="font-heading text-base font-semibold flex items-center gap-2">
                <Inbox className="w-4 h-4" /> Recipients in the next batch
              </div>
              <div className="text-xs text-[#5c6360]">{data.would_email?.length || 0} users</div>
            </div>
            {data.would_email?.length === 0 ? (
              <div className="p-8 text-center text-[#5c6360] text-sm">
                No unread notifications in the last 24h — nothing to send.
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="text-xs uppercase tracking-wider text-stone-500">
                  <tr>
                    <th className="text-left font-medium py-3 px-5">Email</th>
                    <th className="text-left font-medium py-3 px-5">Role</th>
                    <th className="text-left font-medium py-3 px-5">Unread</th>
                    <th className="text-left font-medium py-3 px-5">Latest title</th>
                  </tr>
                </thead>
                <tbody>
                  {data.would_email.map((u) => (
                    <tr key={u.user_id} className="border-t border-border" data-testid={`digest-target-${u.user_id}`}>
                      <td className="py-3 px-5">{u.email}</td>
                      <td className="py-3 px-5 capitalize text-xs">{u.role?.replace(/_/g, " ")}</td>
                      <td className="py-3 px-5 font-mono font-semibold">{u.unread_count}</td>
                      <td className="py-3 px-5 text-xs text-muted-foreground">{u.latest_title || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Test send */}
          <div className="bg-white border border-border rounded-md p-5" data-testid="digest-test-send-card">
            <div className="font-heading text-base font-semibold mb-2">Send a test digest</div>
            <div className="text-xs text-[#5c6360] mb-4">
              Drops a styled test email into <span className="font-mono">db.email_outbox</span> in mock mode.
              When Resend keys are configured (Phase-2), it goes out for real.
            </div>
            <div className="flex flex-wrap items-end gap-3">
              <div className="flex-1 min-w-[260px] space-y-1.5">
                <Label htmlFor="digest-test-email">Recipient email</Label>
                <Input
                  id="digest-test-email"
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="you@nyoksure.co.ke"
                  data-testid="digest-test-email-input"
                  className="bg-white border-[#114232]/15"
                />
              </div>
              <Button
                onClick={sendTest}
                disabled={!testEmail || sending}
                data-testid="digest-test-send-btn"
                className="btn-terracotta rounded-full px-5 h-10 hover:bg-[var(--terracotta-hover)]"
              >
                {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Send test
              </Button>
            </div>
          </div>

          {/* Recent outbox */}
          <div className="bg-white border border-border rounded-md" data-testid="digest-recent-outbox">
            <div className="px-5 py-4 border-b border-border flex items-center justify-between">
              <div className="font-heading text-base font-semibold">Recent outbox (last 10)</div>
              <div className="text-xs text-[#5c6360]">Outbox total: {data.outbox_total}</div>
            </div>
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wider text-stone-500">
                <tr>
                  <th className="text-left font-medium py-3 px-5">To</th>
                  <th className="text-left font-medium py-3 px-5">Subject</th>
                  <th className="text-left font-medium py-3 px-5">Kind</th>
                  <th className="text-left font-medium py-3 px-5">Sent</th>
                </tr>
              </thead>
              <tbody>
                {(data.recent_outbox || []).map((e) => (
                  <tr key={e.id} className="border-t border-border" data-testid={`outbox-row-${e.id}`}>
                    <td className="py-3 px-5">{e.to || "—"}</td>
                    <td className="py-3 px-5">{e.subject || "—"}</td>
                    <td className="py-3 px-5 capitalize text-xs">{e.kind || "—"}</td>
                    <td className="py-3 px-5 text-xs text-muted-foreground font-mono">
                      {e.sent_at ? new Date(e.sent_at).toLocaleString() : "—"}
                    </td>
                  </tr>
                ))}
                {(!data.recent_outbox || data.recent_outbox.length === 0) && (
                  <tr><td colSpan={4} className="py-8 text-center text-muted-foreground">
                    No emails sent yet.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
